import React, { useEffect, useRef, useCallback } from 'react'

interface CustomPaginationProps {
  currentPage: number
  totalPages: number
  totalCount: number
  hasNextPage: boolean
  hasPrevPage: boolean
  onPageChange: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  pageSize?: number
  pageSizeOptions?: number[]
  className?: string
  minWidth?: string
  disabled?: boolean
  transparency?: 'low' | 'medium' | 'high' | 'none'
  showPageInfo?: boolean
  maxVisiblePages?: number
}

export default function CustomPagination({
  currentPage,
  totalPages,
  totalCount,
  hasNextPage,
  hasPrevPage,
  onPageChange,
  onPageSizeChange,
  pageSize = 50,
  pageSizeOptions = [10, 25, 50, 100],
  className = '',
  minWidth = "min-w-[300px]",
  disabled = false,
  transparency = 'medium',
  showPageInfo = true,
  maxVisiblePages = 5
}: CustomPaginationProps) {
  const paginationRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = React.useState(false)
  const [pageWindowStart, setPageWindowStart] = React.useState(1)
  const [isPageSizeOpen, setIsPageSizeOpen] = React.useState(false)

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (paginationRef.current && !paginationRef.current.contains(event.target as Node)) {
      setIsOpen(false)
      setIsPageSizeOpen(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen || isPageSizeOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen, isPageSizeOpen, handleClickOutside])

  // Initialize page window when component mounts or current page changes
  useEffect(() => {
    if (totalPages <= maxVisiblePages) {
      setPageWindowStart(1)
    } else {
      // Center the window around the current page
      const start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
      const end = Math.min(totalPages, start + maxVisiblePages - 1)
      const adjustedStart = end === totalPages ? Math.max(1, end - maxVisiblePages + 1) : start
      setPageWindowStart(adjustedStart)
    }
  }, [currentPage, totalPages, maxVisiblePages])

  const handlePageSizeChange = (newPageSize: number) => {
    if (disabled || !onPageSizeChange) return
    onPageSizeChange(newPageSize)
    setIsPageSizeOpen(false)
  }

  const handlePageSizeToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return
    setIsPageSizeOpen(!isPageSizeOpen)
    setIsOpen(false)
  }

  // Show simplified view only for small datasets (≤10 items) AND when no page size control is needed
  // For larger datasets or when page size control is needed, show full pagination
  if (totalPages <= 1 && totalCount <= pageSizeOptions[0] && !onPageSizeChange) {
    // Show simplified view for single page with small dataset
    return (
      <div className={className}>
        <div className="relative" ref={paginationRef}>
          <div
            className={`min-h-[42px] ${minWidth} border border-gray-300 rounded-md p-2 relative ${
              disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-default'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing {totalCount} of {totalCount} results
              </span>
              {onPageSizeChange && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Show:</span>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={handlePageSizeToggle}
                      disabled={disabled}
                      className={`text-sm px-2 py-1 rounded border border-gray-200 hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'
                      }`}
                    >
                      {pageSize}
                      <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Page size options dropdown */}
                    <div
                      className={`${isPageSizeOpen ? '' : 'hidden'} absolute z-20 mt-1 left-0 ${
                        transparency === 'none'
                          ? 'bg-white border border-gray-200'
                          : transparency === 'low'
                          ? 'bg-gradient-to-b from-white/80 to-white/70 backdrop-blur-sm border border-gray-200/60'
                          : transparency === 'high'
                          ? 'bg-gradient-to-b from-white/20 to-white/10 backdrop-blur-lg border border-gray-200/20'
                          : 'bg-gradient-to-b from-white/40 to-white/30 backdrop-blur-md border border-gray-200/30'
                      } rounded-md shadow-lg min-w-[60px]`}
                    >
                      {pageSizeOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => handlePageSizeChange(option)}
                          disabled={disabled}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-md last:rounded-b-md ${
                            option === pageSize ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalCount)

  const handlePageChange = (page: number) => {
    if (disabled) return
    if (page >= 1 && page <= totalPages) {
      onPageChange(page)
      setIsOpen(false)
    }
  }

  const handleToggle = () => {
    if (disabled) return
    setIsOpen(!isOpen)
  }

  const renderPageNumbers = () => {
    const pages = []
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Use the sliding window start position
      const start = Math.max(1, pageWindowStart)
      const end = Math.min(totalPages, start + maxVisiblePages - 1)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
    }
    return pages
  }

  const getPageInfoDisplay = () => {
    if (!showPageInfo) {
      return <span className="text-sm text-gray-900">Page {currentPage} of {totalPages}</span>
    }
    
    return (
      <div className="flex flex-col">
        <span className="text-sm text-gray-900 font-medium">
          Page {currentPage} of {totalPages}
        </span>
        <span className="text-xs text-gray-500">
          Showing {startItem}-{endItem} of {totalCount}
        </span>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="relative" ref={paginationRef}>
        {/* Main pagination display */}
        <div 
          className={`min-h-[42px] ${minWidth} border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 p-2 relative ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'
          }`}
          onClick={handleToggle}
        >
          <div className="flex items-center justify-between">
            {getPageInfoDisplay()}
            
            {/* Entries per page dropdown */}
            {onPageSizeChange && (
              <div className="flex items-center gap-2 mr-8">
                <span className="text-xs text-gray-500">Show:</span>
                <div className="relative">
                  <button
                    type="button"
                    onClick={handlePageSizeToggle}
                    disabled={disabled}
                    className={`text-sm px-2 py-1 rounded border border-gray-200 hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                      disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'
                    }`}
                  >
                    {pageSize}
                    <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Page size options dropdown */}
                  <div 
                    className={`${isPageSizeOpen ? '' : 'hidden'} absolute z-20 mt-1 left-0 ${
                      transparency === 'none' 
                        ? 'bg-white border border-gray-200' 
                        : transparency === 'low'
                        ? 'bg-gradient-to-b from-white/80 to-white/70 backdrop-blur-sm border border-gray-200/60'
                        : transparency === 'high'
                        ? 'bg-gradient-to-b from-white/20 to-white/10 backdrop-blur-lg border border-gray-200/20'
                        : 'bg-gradient-to-b from-white/40 to-white/30 backdrop-blur-md border border-gray-200/30'
                    } rounded-md shadow-lg min-w-[60px]`}
                  >
                    {pageSizeOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handlePageSizeChange(option)}
                        disabled={disabled}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-md last:rounded-b-md ${
                          option === pageSize ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Dropdown arrow */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <button
                type="button"
                onClick={handleToggle}
                disabled={disabled}
                className={`text-gray-400 hover:text-gray-600 focus:outline-none ${
                  disabled ? 'cursor-not-allowed' : ''
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        {/* Page selection dropdown */}
        <div 
          className={`${isOpen ? '' : 'hidden'} absolute z-10 mt-1 left-0 right-0 ${
            transparency === 'none' 
              ? 'bg-white border border-gray-200' 
              : transparency === 'low'
              ? 'bg-gradient-to-b from-white/80 to-white/70 backdrop-blur-sm border border-gray-200/60'
              : transparency === 'high'
              ? 'bg-gradient-to-b from-white/20 to-white/10 backdrop-blur-lg border border-gray-200/20'
              : 'bg-gradient-to-b from-white/40 to-white/30 backdrop-blur-md border border-gray-200/30'
          } rounded-md shadow-xl max-h-60 overflow-auto`}
        >
          <div className="p-3 w-full">
            {/* Quick navigation */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                disabled={!hasPrevPage || disabled}
                className={`text-sm px-2 py-1 rounded transition-colors ${
                  !hasPrevPage || disabled
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                }`}
              >
                First
              </button>
              
              <button
                type="button"
                onClick={() => handlePageChange(totalPages)}
                disabled={!hasNextPage || disabled}
                className={`text-sm px-2 py-1 rounded transition-colors ${
                  !hasNextPage || disabled
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                }`}
              >
                Last
              </button>
            </div>
            
            {/* Page numbers with navigation on same line */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const pages = renderPageNumbers()
                  const firstPage = pages[0]
                  if (firstPage > 1) {
                    // Slide the page window left without changing the current page
                    const newStart = Math.max(1, firstPage - maxVisiblePages)
                    setPageWindowStart(newStart)
                  }
                }}
                disabled={renderPageNumbers()[0] <= 1 || disabled}
                className={`p-1 rounded transition-colors ${
                  renderPageNumbers()[0] <= 1 || disabled
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
                            {/* Page numbers grid */}
              <div className="grid grid-cols-5 gap-1 flex-1">
                {renderPageNumbers().map((page) => {
                  const isCurrentPage = page === currentPage
                  
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => handlePageChange(page)}
                      disabled={disabled}
                      className={`p-2 text-sm rounded transition-all duration-200 ease-out ${
                        isCurrentPage
                          ? transparency === 'none'
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : transparency === 'low'
                            ? 'bg-gradient-to-r from-blue-50/90 to-blue-100/80 backdrop-blur-sm text-blue-700 font-medium'
                            : transparency === 'high'
                            ? 'bg-gradient-to-r from-blue-50/40 to-blue-100/30 backdrop-blur-lg text-blue-700 font-medium'
                            : 'bg-gradient-to-r from-blue-50/70 to-blue-100/50 backdrop-blur-sm text-blue-700 font-medium'
                          : transparency === 'none'
                          ? 'text-gray-700 hover:bg-gray-50'
                          : transparency === 'low'
                          ? 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50/80 hover:to-gray-100/70 hover:backdrop-blur-sm'
                          : transparency === 'high'
                          ? 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50/30 hover:to-gray-100/20 hover:backdrop-blur-lg'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50/60 hover:to-gray-100/40 hover:backdrop-blur-sm'
                      }`}
                    >
                      {page}
                    </button>
                  )
                })}
              </div>
              
              <button
                type="button"
                onClick={() => {
                  const pages = renderPageNumbers()
                  const lastPage = pages[pages.length - 1]
                  if (lastPage < totalPages) {
                    // Slide the page window right without changing the current page
                    const newStart = Math.max(1, lastPage + 1)
                    setPageWindowStart(newStart)
                  }
                }}
                disabled={renderPageNumbers()[renderPageNumbers().length - 1] >= totalPages || disabled}
                className={`p-1 rounded transition-colors ${
                  renderPageNumbers()[renderPageNumbers().length - 1] >= totalPages || disabled
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Page info in dropdown */}
            <div className="mt-3 pt-3 border-t border-gray-200/50">
              <div className="text-xs text-gray-500 text-center">
                Showing {startItem}-{endItem} of {totalCount} items
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 